import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import accountAccessGrantsService, {
  type ActorAccountAccessGrant,
} from "../services/accountAccessGrantsService";

export const actorAccountAccessGrantQueryKeys = {
  all: ["security", "account-access-grants"] as const,
  actor: (actorUserId: string) => [
    "security",
    "account-access-grants",
    "actor",
    actorUserId,
  ] as const,
};

export function isSelfAccountAccessGrant(grant: ActorAccountAccessGrant): boolean {
  return grant.actorRelationship === "SELF"
    || grant.approvalMode === "SYSTEM_SELF"
    || grant.granteeUser.userId === grant.targetUser.userId;
}

export function isPendingAccountAccessDecision(
  grant: ActorAccountAccessGrant
): boolean {
  return !isSelfAccountAccessGrant(grant)
    && grant.actorRelationship === "TARGET"
    && grant.approvalMode === "TARGET_OWNER"
    && grant.status === "PENDING_APPROVAL";
}

export function getPendingAccountAccessDecisionCount(
  grants: readonly ActorAccountAccessGrant[]
): number {
  return grants.filter(isPendingAccountAccessDecision).length;
}

export function useActorAccountAccessGrants(enabled = true) {
  const { actorUserId } = useAuth();
  const securityActorUserId = actorUserId ?? "";

  return useQuery<ActorAccountAccessGrant[], Error>({
    queryKey: actorAccountAccessGrantQueryKeys.actor(securityActorUserId),
    queryFn: async () => {
      const response = await accountAccessGrantsService.list();
      return response.items;
    },
    enabled: enabled && securityActorUserId.length > 0,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}
